import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { AddressBottomsheetMode } from 'client/app/app.constants';
import { AppConfig } from 'client/app/app.config';
import { EventLoggerService } from '@services/event-logger-service';

export interface UserAddressBottomsheetData {
  mode?: AddressBottomsheetMode;
  mobileNumber?: string;
  otpLength?: number;
  resendSeconds?: number;
  onSendOtp?: (mobileNumber: string) => boolean | Promise<boolean> | void;
  onVerifyOtp?: (otp: string, mobileNumber: string) => boolean | Promise<boolean> | void;
  onEditNumber?: () => void;
  onResendOtp?: (mobileNumber: string) => boolean | Promise<boolean> | void;
  skipInitialSend?: boolean;
}

@Component({
  selector: 'user-address-bottomsheet',
  templateUrl: './user-address-bottomsheet.html',
  standalone: false,
})
export class UserAddressBottomsheetComponent implements OnInit, OnDestroy {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  @Input() mode?: AddressBottomsheetMode;
  @Input() mobileNumber?: string;
  @Input() otpLength?: number;
  @Input() resendSeconds?: number;

  @Output() sendOtp = new EventEmitter<string>();
  @Output() verifyOtp = new EventEmitter<{ otp: string; mobileNumber: string }>();
  @Output() editNumber = new EventEmitter<void>();
  @Output() resendOtp = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  otpDigits: string[] = [];
  timerRemaining: number = 0;
  verifying: boolean = false;
  private timerId: any;

  trackByIndex(index: number): number {
    return index;
  }

  constructor(
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public data: UserAddressBottomsheetData | null,
    @Optional() private readonly bottomSheetRef: MatBottomSheetRef<UserAddressBottomsheetComponent> | null,
    private readonly eventLogger: EventLoggerService,
  ) {
    const length = this.resolvedOtpLength;
    this.otpDigits = Array.from({ length }).map(() => '');
    this.timerRemaining = this.resolvedResendSeconds;
  }

  async ngOnInit(): Promise<void> {
    const mode = this.resolvedMode === AppConfig.Shared.AddressBook.BottomsheetMode.Otp ? 'otp' : 'mobile';
    this.eventLogger.cleverTapEvent('popup', JSON.stringify({
      name: 'shiprocket_login_popup',
      type: mode,
    }));

    if (this.resolvedMode === AppConfig.Shared.AddressBook.BottomsheetMode.Otp && this.resolvedMobileNumber.length === 10) {
      this.startTimer();
      if (!this.data?.skipInitialSend) await this.onClickSendOtp();
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  get resolvedMode(): AddressBottomsheetMode {
    return this.mode ?? this.data?.mode ?? AppConfig.Shared.AddressBook.BottomsheetMode.Mobile;
  }

  set resolvedMode(next: AddressBottomsheetMode) {
    this.mode = next;
  }

  get resolvedOtpLength(): number {
    return this.otpLength ?? this.data?.otpLength ?? 6;
  }

  get resolvedResendSeconds(): number {
    return this.resendSeconds ?? this.data?.resendSeconds ?? 30;
  }

  get resolvedMobileNumber(): string {
    return this.sanitizeMobile(this.mobileNumber ?? this.data?.mobileNumber);
  }

  set resolvedMobileNumber(value: string) {
    this.mobileNumber = value;
  }

  get canSendOtp(): boolean {
    return this.resolvedMobileNumber.length === 10;
  }

  get otpValue(): string {
    return this.otpDigits.join('');
  }

  get canVerifyOtp(): boolean {
    return this.otpValue.length === this.resolvedOtpLength && !this.otpDigits.some((d) => !d);
  }

  close(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'shiprocket_close_popup' }));
    this.closed.emit();
    this.bottomSheetRef?.dismiss();
  }

  async onClickSendOtp(): Promise<void> {
    if (!this.canSendOtp) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'shiprocket_send_otp' }));
    const mobile = this.resolvedMobileNumber;
    const wasAlreadyInOtpMode = this.resolvedMode === AppConfig.Shared.AddressBook.BottomsheetMode.Otp;

    this.sendOtp.emit(mobile);
    if (this.data?.onSendOtp) {
      const result = await this.data.onSendOtp(mobile);
      if (result === false) {
        return;
      }
    }

    if (!wasAlreadyInOtpMode) {
      this.resolvedMode = AppConfig.Shared.AddressBook.BottomsheetMode.Otp;
      this.resetOtp();
    }
    this.startOtpTimer();
  }

  async onClickVerifyOtp(): Promise<void> {
    if (!this.canVerifyOtp || this.verifying) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'shiprocket_verify_otp' }));
    const payload = { otp: this.otpValue, mobileNumber: this.resolvedMobileNumber };
    this.verifyOtp.emit(payload);

    if (this.data?.onVerifyOtp) {
      this.verifying = true;
      try {
        const result = await this.data.onVerifyOtp(payload.otp, payload.mobileNumber);
        if (result === false) {
          this.verifying = false;
          return;
        }
      } catch {
        this.verifying = false;
        return;
      }
    }

    this.bottomSheetRef?.dismiss(payload);
  }

  onClickEdit(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'shiprocket_edit_number' }));
    this.editNumber.emit();
    this.data?.onEditNumber?.();
    this.resolvedMode = AppConfig.Shared.AddressBook.BottomsheetMode.Mobile;
    this.stopTimer();
  }

  async onClickResend(): Promise<void> {
    if (this.timerRemaining > 0 || !this.canSendOtp) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'shiprocket_resend_otp' }));
    const mobile = this.resolvedMobileNumber;
    this.resendOtp.emit(mobile);
    if (this.data?.onResendOtp) {
      const result = await this.data.onResendOtp(mobile);
      if (result === false) {
        return;
      }
    }

    this.resetOtp();
    try {
      const active = globalThis?.document?.activeElement as HTMLElement | null;
      if (active && active.tagName === 'INPUT') active.blur();
    } catch (_) {
    }

    this.startOtpTimer();
  }

  onMobileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.resolvedMobileNumber = this.sanitizeMobile(input.value);
    input.value = this.resolvedMobileNumber;
  }

  onOtpKeydown(event: KeyboardEvent, idx: number): void {
    const key = event.key;
    if (key === 'Backspace') {
      const inputEl = event.target as HTMLInputElement | null;
      const hasCurrentValue = !!(inputEl?.value);

      if (hasCurrentValue || this.otpDigits[idx]) {
        this.otpDigits[idx] = '';
        if (inputEl) inputEl.value = '';
        this.syncOtpInputs();
        this.focusOtp(idx, false);
      } else if (idx > 0) {
        this.otpDigits[idx - 1] = '';
        this.syncOtpInputs();
        this.focusOtp(idx - 1, false);
      } else {
        this.syncOtpInputs();
        this.focusOtp(0, false);
      }
      event.preventDefault();
      return;
    }
    if (key === 'ArrowLeft') {
      if (idx > 0) this.focusOtp(idx - 1);
      event.preventDefault();
      return;
    }
    if (key === 'ArrowRight') {
      if (idx < this.resolvedOtpLength - 1) this.focusOtp(idx + 1);
      event.preventDefault();
      return;
    }
    if (key.length === 1 && !/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  onOtpInput(event: Event, idx: number): void {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '');
    if (!digits) {
      this.otpDigits[idx] = '';
      input.value = '';
      return;
    }

    const arr = digits.split('');
    let cursor = idx;
    for (const d of arr) {
      if (cursor >= this.resolvedOtpLength) break;
      this.otpDigits[cursor] = d;
      cursor += 1;
    }
    this.syncOtpInputs();

    if (cursor < this.resolvedOtpLength) this.focusOtp(cursor);
    else this.focusOtp(this.resolvedOtpLength - 1);
  }

  /**
   * Only allow focusing the "next" OTP box (first empty).
   * If all boxes are filled, keep focus on the last box.
   */
  getFirstEditableOtpIndex(): number {
    const firstEmpty = this.otpDigits.findIndex((d) => !d);
    return firstEmpty === -1 ? Math.max(0, this.resolvedOtpLength - 1) : firstEmpty;
  }

  onOtpAttemptFocus(event: Event, idx: number): void {
    const allowedIdx = this.getFirstEditableOtpIndex();
    if (idx === allowedIdx) return;

    // Prevent the user from focusing arbitrary boxes
    event.preventDefault?.();
    event.stopPropagation?.();
    this.focusOtp(allowedIdx, false);
  }

  private syncOtpInputs(): void {
    if (!this.otpInputs) return;
    const inputs = this.otpInputs.toArray();
    inputs.forEach((ref, i) => {
      const el = ref.nativeElement;
      const v = this.otpDigits[i] || '';
      if (el.value !== v) el.value = v;
    });
  }

  private focusOtp(idx: number, shouldSelect: boolean = true): void {
    const input = this.otpInputs?.toArray()?.[idx]?.nativeElement;
    if (!input) return;
    input.focus();
    if (shouldSelect) input.select?.();
  }

  private resetOtp(): void {
    this.otpDigits = Array.from({ length: this.resolvedOtpLength }).map(() => '');
    this.syncOtpInputs();
  }

  private startOtpTimer(): void {
    this.timerRemaining = this.resolvedResendSeconds;
    this.startTimer();
  }

  private sanitizeMobile(value?: string): string {
    return (value || '').replace(/\D/g, '').slice(-10);
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerId = setInterval(() => {
      this.timerRemaining = Math.max(0, this.timerRemaining - 1);
      if (this.timerRemaining === 0) this.stopTimer();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }
}
